// apps/cop/src/components/SplashScreen.tsx
// CONTRACT:C4-DESIGN-TOKENS.1.0
//
// COP — pre-login SPLASH gate (Phase 3 entrance). Shown BEFORE the existing
// LoginScreen; "Enter the TDF COP" hands off to the unchanged idplite login. The
// second door, "Open Legacy WebTAK", is a one-way exit out of the COP to the legacy
// client (VITE_WEBTAK_URL) behind a brief "you are leaving the TDF COP" interstitial.
// If VITE_WEBTAK_URL is unset, that door renders DISABLED ("coming soon") — never broken.
//
// BRAND ENTRANCE (graduation pass): the composition is the brand mark's own geometry
// writ large — a teal spine hairline descends from above into the sealed-object
// hexagon (BrandMark), exactly as the mark's internal keyline enters its payload;
// the Saira tracked wordmark and the choice card hang beneath it on one axis.
// Typography-led, hairline-structured, flat near-black ground — no decorative
// gradients. All values ride the C4 semantic tokens (var(--token, fallback)).
//
// DEMO — internal / non-operational (the caution bar is Will's posture marker; it
// stays until HE pulls it). Invariant: .splash__choice--primary is an e2e handle
// (clearance-journey / cross-platform-scenario click it) — the class must survive.

import { useEffect, useState } from "react";
import { BrandMark } from "./BrandMark.tsx";
import "./splash.css";

const WEBTAK_URL = ((import.meta.env?.VITE_WEBTAK_URL as string | undefined) ?? "").trim();

export function SplashScreen({ onEnter }: { onEnter: () => void }): JSX.Element {
  // When set, we are mid-handoff to Legacy WebTAK: show the interstitial, then redirect.
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (!leaving || !WEBTAK_URL) return;
    const t = window.setTimeout(() => {
      window.location.assign(WEBTAK_URL);
    }, 1300);
    return () => window.clearTimeout(t);
  }, [leaving]);

  if (leaving) {
    return (
      <div className="splash">
        <div className="splash__interstitial">
          <BrandMark className="splash__interstitial-mark" size={28} />
          <div className="splash__eyebrow">Leaving the TDF COP</div>
          <div className="splash__leaving-title">Handing you to Legacy WebTAK…</div>
          <div className="splash__sub">
            You are leaving the protected TDF COP for the legacy client. Classification-aware
            filtering does not apply on the legacy side.
          </div>
          <button type="button" className="splash__link" onClick={() => setLeaving(false)}>
            ← Stay in the TDF COP
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="splash">
      <div className="splash__demobar">DEMO — INTERNAL / NON-OPERATIONAL</div>

      <div className="splash__stage">
        {/* The spine descends into the seal — the mark's keyline, at page scale. */}
        <div className="splash__spine" aria-hidden="true" />

        <div className="splash__brand">
          <BrandMark className="splash__mark" size={46} />
          <div className="splash__wordmark">TAK · TDF</div>
          <div className="splash__wordmark-sub">Coalition COP</div>
        </div>

        <div className="splash__card">
          <h1 className="splash__title">Choose your entrance</h1>
          <div className="splash__sub">
            A classification-aware common operating picture. Sign in to load your clearance, or
            cross over to the legacy WebTAK client.
          </div>

          <div className="splash__choices">
            <button
              type="button"
              className="splash__choice splash__choice--primary"
              onClick={onEnter}
            >
              <span className="splash__choice-title">
                Enter the TDF COP
                <span className="splash__choice-arrow" aria-hidden="true">
                  →
                </span>
              </span>
              <span className="splash__choice-sub">
                Sign in with a persona; your clearance shapes the picture.
              </span>
            </button>

            <button
              type="button"
              className="splash__choice"
              disabled={!WEBTAK_URL}
              onClick={() => setLeaving(true)}
            >
              <span className="splash__choice-title">Open Legacy WebTAK</span>
              <span className="splash__choice-sub">
                {WEBTAK_URL ? "Leave the COP for the legacy client." : "coming soon"}
              </span>
            </button>
          </div>
        </div>

        <div className="splash__footline">TDF-secured · fail-closed by construction</div>
      </div>
    </div>
  );
}
